import React from "react";

import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";

import logo from "./logoo.png";

// ============================================================
// COLORS
// ============================================================

const COLORS = {
  navy: "#1B2A41",
  gold: "#C8A74E",
  goldLight: "#F5EEDB",
  white: "#FFFFFF",
  background: "#F7F8FA",
  border: "#E2E6EB",
  text: "#1F2937",
  muted: "#6B7280",
  lightText: "#9CA3AF",
  success: "#15803D",
  successBg: "#ECFDF3",
  warningBg: "#FFF9E8",
  warningBorder: "#E8D59A",
};

// ============================================================
// STYLES
// ============================================================

const styles = StyleSheet.create({

  page: {
    paddingTop: 22,
    paddingHorizontal: 30,
    paddingBottom: 18,
    backgroundColor: COLORS.background,
    fontSize: 9,
    color: COLORS.text,
  },

  // ==========================================================
  // HEADER
  // ==========================================================

  header: {
    flexDirection: "row",
    alignItems: "center",
    paddingBottom: 9,
    marginBottom: 12,
    borderBottom: "2 solid #C8A74E",
  },

  logo: {
    width: 48,
    height: 48,
    marginRight: 11,
  },

  companyInfo: {
    flex: 1,
  },

  companyName: {
    fontSize: 14,
    fontWeight: "bold",
    color: COLORS.navy,
    letterSpacing: 0.6,
  },

  subtitle: {
    fontSize: 8,
    marginTop: 2,
    color: COLORS.muted,
  },

  documentBadge: {
    paddingVertical: 5,
    paddingHorizontal: 8,
    borderRadius: 10,
    backgroundColor: COLORS.goldLight,
    border: "1 solid #E5D39A",
  },

  documentBadgeText: {
    fontSize: 6.5,
    fontWeight: "bold",
    color: COLORS.navy,
    letterSpacing: 0.5,
  },

  // ==========================================================
  // ROYALTY CARD
  // ==========================================================

  cardSection: {
    padding: 13,
    borderRadius: 10,
    marginBottom: 11,
    backgroundColor: COLORS.white,
    border: "1 solid #DCE1E7",
    borderLeft: "4 solid #C8A74E",
  },

  cardHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
  },

  cardTitle: {
    fontSize: 10,
    fontWeight: "bold",
    color: COLORS.navy,
    letterSpacing: 0.6,
  },

  activeBadge: {
    paddingVertical: 4,
    paddingHorizontal: 7,
    borderRadius: 8,
    backgroundColor: COLORS.successBg,
    border: "1 solid #BBE7C9",
  },

  activeBadgeText: {
    fontSize: 6,
    fontWeight: "bold",
    color: COLORS.success,
  },

  cardContent: {
    flexDirection: "row",
    alignItems: "center",
  },

  cardDetails: {
    flex: 1,
    paddingRight: 12,
  },

  cardName: {
    fontSize: 17,
    fontWeight: "bold",
    color: COLORS.navy,
    marginBottom: 8,
  },

  cardRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 4,
  },

  cardLabel: {
    width: 75,
    color: COLORS.muted,
    fontSize: 6.5,
    fontWeight: "bold",
    letterSpacing: 0.4,
  },

  cardValue: {
    color: COLORS.text,
    fontSize: 8,
    fontWeight: "bold",
  },

  pointsBox: {
    marginTop: 5,
    paddingVertical: 5,
    paddingHorizontal: 8,
    backgroundColor: COLORS.goldLight,
    borderRadius: 5,
    border: "1 solid #E9D99F",
  },

  pointsLabel: {
    fontSize: 6,
    color: COLORS.muted,
    marginBottom: 1,
  },

  pointsValue: {
    fontSize: 12,
    fontWeight: "bold",
    color: COLORS.navy,
  },

  // ==========================================================
  // QR
  // ==========================================================

  qrContainer: {
    width: 105,
    alignItems: "center",
    justifyContent: "center",
    paddingLeft: 5,
  },

  qrBackground: {
    width: 88,
    height: 88,
    padding: 5,
    backgroundColor: COLORS.white,
    borderRadius: 6,
    border: "1 solid #D9DEE5",
  },

  qrCode: {
    width: 78,
    height: 78,
  },

  qrText: {
    fontSize: 5.5,
    color: COLORS.muted,
    marginTop: 4,
    textAlign: "center",
    fontWeight: "bold",
  },

  // ==========================================================
  // GENERAL SECTIONS
  // ==========================================================

  section: {
    marginBottom: 8,
    padding: 9,
    backgroundColor: COLORS.white,
    border: "1 solid #E1E5EA",
    borderRadius: 7,
  },

  title: {
    fontSize: 8.5,
    fontWeight: "bold",
    marginBottom: 5,
    color: COLORS.navy,
    letterSpacing: 0.5,
    paddingBottom: 4,
    borderBottom: "1 solid #E8EBEF",
  },

  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 2,
  },

  label: {
    fontWeight: "bold",
    color: COLORS.muted,
    fontSize: 7,
  },

  value: {
    color: COLORS.text,
    fontSize: 7.5,
  },

  // ==========================================================
  // SECURITY
  // ==========================================================

  securitySection: {
    marginBottom: 8,
    padding: 9,
    backgroundColor: "#FAFBFC",
    border: "1 solid #E1E5EA",
    borderRadius: 7,
  },

  securityTitle: {
    fontSize: 8.5,
    fontWeight: "bold",
    marginBottom: 5,
    color: COLORS.navy,
    letterSpacing: 0.4,
  },

  securityRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 3,
  },

  securityBullet: {
    width: 10,
    fontSize: 7,
    fontWeight: "bold",
    color: COLORS.gold,
  },

  securityText: {
    flex: 1,
    fontSize: 7,
    color: COLORS.text,
    lineHeight: 1.2,
  },

  // ==========================================================
  // WARNING
  // ==========================================================

  warningBox: {
    padding: 8,
    borderRadius: 6,
    backgroundColor: COLORS.warningBg,
    border: "1 solid #E8D59A",
  },

  warningHeader: {
    fontSize: 7.5,
    fontWeight: "bold",
    color: COLORS.navy,
    marginBottom: 2,
  },

  warningText: {
    fontSize: 6.8,
    color: COLORS.text,
    lineHeight: 1.2,
  },

  // ==========================================================
  // FOOTER
  // ==========================================================

  footer: {
    marginTop: 9,
    alignItems: "center",
  },

  signatureText: {
    fontSize: 6.5,
    color: COLORS.muted,
    textAlign: "center",
  },

  line: {
    marginTop: 9,
    borderBottom: "1 solid #9CA3AF",
    width: "35%",
  },

  generatedText: {
    marginTop: 4,
    fontSize: 5.5,
    color: COLORS.lightText,
    textAlign: "center",
  },

  footerAccent: {
    marginTop: 4,
    width: 35,
    height: 2,
    backgroundColor: COLORS.gold,
    borderRadius: 2,
  },
});

// ============================================================
// DOCUMENT
// ============================================================

const CustomerCardInfoPDF = ({
  customer,
  qrCode,
}) => {

  const points =
    customer?.reward_cycle_points ??
    customer?.total_points ??
    0;

  return (
    <Document>

      <Page
        size="A4"
        style={styles.page}
        wrap={false}
      >

        {/* ====================================================
            HEADER
        ===================================================== */}

        <View
          style={styles.header}
          wrap={false}
        >

          <Image
            style={styles.logo}
            src={logo}
          />

          <View style={styles.companyInfo}>

            <Text style={styles.companyName}>
              ZULUWHISKEY SHIPPING COMPANY
            </Text>

            <Text style={styles.subtitle}>
              Official Customer Royalty & Card Document
            </Text>

          </View>

          <View style={styles.documentBadge}>

            <Text style={styles.documentBadgeText}>
              ROYALTY MEMBER
            </Text>

          </View>

        </View>

        {/* ====================================================
            ROYALTY CARD
        ===================================================== */}

        <View
          style={styles.cardSection}
          wrap={false}
        >

          <View style={styles.cardHeader}>

            <Text style={styles.cardTitle}>
              ZULUWHISKEY ROYALTY CARD
            </Text>

            <View style={styles.activeBadge}>

              <Text style={styles.activeBadgeText}>
                ● ACTIVE MEMBER
              </Text>

            </View>

          </View>

          <View style={styles.cardContent}>

            {/* CUSTOMER */}

            <View style={styles.cardDetails}>

              <Text style={styles.cardName}>
                {customer?.full_name || "Customer"}
              </Text>

              <View style={styles.cardRow}>

                <Text style={styles.cardLabel}>
                  CARD NUMBER
                </Text>

                <Text style={styles.cardValue}>
                  ZW-{customer?.id}
                </Text>

              </View>

              <View style={styles.cardRow}>

                <Text style={styles.cardLabel}>
                  PHONE
                </Text>

                <Text style={styles.cardValue}>
                  {customer?.phone || "N/A"}
                </Text>

              </View>

              <View style={styles.cardRow}>

                <Text style={styles.cardLabel}>
                  STATUS
                </Text>

                <Text style={styles.cardValue}>
                  ACTIVE
                </Text>

              </View>

              <View style={styles.pointsBox}>

                <Text style={styles.pointsLabel}>
                  ROYALTY BALANCE
                </Text>

                <Text style={styles.pointsValue}>
                  {/* {points} POINTS */}
                  ALWAYS SCAN THE QR CODE ON YOUR CARD TO CHECK YOUR BALLANCE
                </Text>

              </View>

            </View>

            {/* QR */}

            {qrCode && (

              <View style={styles.qrContainer}>

                <View style={styles.qrBackground}>

                  <Image
                    style={styles.qrCode}
                    src={qrCode}
                  />

                </View>

                <Text style={styles.qrText}>
                  SCAN TO IDENTIFY CUSTOMER
                </Text>

              </View>

            )}

          </View>

        </View>

        {/* ====================================================
            INFORMATION ROW
        ===================================================== */}

        <View
          style={{
            flexDirection: "row",
            gap: 8,
          }}
          wrap={false}
        >

          {/* CUSTOMER INFORMATION */}

          <View
            style={[
              styles.section,
              {
                flex: 1,
              },
            ]}
            wrap={false}
          >

            <Text style={styles.title}>
              CUSTOMER INFORMATION
            </Text>

            <View style={styles.row}>

              <Text style={styles.label}>
                Full Name
              </Text>

              <Text style={styles.value}>
                {customer?.full_name || "N/A"}
              </Text>

            </View>

            <View style={styles.row}>

              <Text style={styles.label}>
                Phone
              </Text>

              <Text style={styles.value}>
                {customer?.phone || "N/A"}
              </Text>

            </View>

            <View style={styles.row}>

              <Text style={styles.label}>
                Email
              </Text>

              <Text style={styles.value}>
                {customer?.email || "N/A"}
              </Text>

            </View>

          </View>

          {/* CARD DETAILS */}

          <View
            style={[
              styles.section,
              {
                flex: 1,
              },
            ]}
            wrap={false}
          >

            <Text style={styles.title}>
              CARD DETAILS
            </Text>

            <View style={styles.row}>

              <Text style={styles.label}>
                Customer ID
              </Text>

              <Text style={styles.value}>
                ZW-{customer?.id}
              </Text>

            </View>

            <View style={styles.row}>

              <Text style={styles.label}>
                Card PIN
              </Text>

              <Text style={styles.value}>
                {customer?.card_pin || "N/A"}
              </Text>

            </View>

            <View style={styles.row}>

              <Text style={styles.label}>
                Card Status
              </Text>

              <Text style={styles.value}>
                ACTIVE
              </Text>

            </View>

            <View style={styles.row}>

              <Text style={styles.label}>
                Royalty Points
              </Text>

              <Text style={styles.value}>
                {points}
              </Text>

            </View>

          </View>

        </View>

        {/* ====================================================
            SECURITY
        ===================================================== */}

        <View
          style={styles.securitySection}
          wrap={false}
        >

          <Text style={styles.securityTitle}>
            IMPORTANT SECURITY INFORMATION
          </Text>

          <View style={styles.securityRow}>

            <Text style={styles.securityBullet}>
              •
            </Text>

            <Text style={styles.securityText}>
              Do not share your PIN with anyone.
            </Text>

          </View>

          <View style={styles.securityRow}>

            <Text style={styles.securityBullet}>
              •
            </Text>

            <Text style={styles.securityText}>
              This card is personal and non-transferable.
            </Text>

          </View>

          <View style={styles.securityRow}>

            <Text style={styles.securityBullet}>
              •
            </Text>

            <Text style={styles.securityText}>
              Report lost or compromised cards immediately.
            </Text>

          </View>

          <View style={styles.securityRow}>

            <Text style={styles.securityBullet}>
              •
            </Text>

            <Text style={styles.securityText}>
              Zuluwhiskey Shipping Company is not responsible
              for unauthorized use.
            </Text>

          </View>

        </View>

        {/* ====================================================
            WARNING
        ===================================================== */}

        <View
          style={styles.warningBox}
          wrap={false}
        >

          <Text style={styles.warningHeader}>
            IMPORTANT NOTICE
          </Text>

          <Text style={styles.warningText}>
            This document serves as an official customer
            identification and Royalty card record. Any misuse
            may result in suspension of account privileges.
          </Text>

        </View>

        {/* ====================================================
            FOOTER
        ===================================================== */}

        <View
          style={styles.footer}
          wrap={false}
        >

          <Text style={styles.signatureText}>
            Authorized Signature
          </Text>

          <Text style={styles.signatureText}>
            ZULUWHISKEY SHIPPING COMPANY
          </Text>

          <View style={styles.line} />

          <Text style={styles.generatedText}>
            This is a system-generated document
          </Text>

          <View style={styles.footerAccent} />

        </View>

      </Page>

    </Document>
  );
};

export default CustomerCardInfoPDF;